import { cityKeyFromText } from './travelLegs';

function toNumOrNull(v) {
	const n = Number(v);
	return Number.isFinite(n) ? n : null;
}

// A coordinate is only valid if both parts are finite AND it isn't the (0,0)
// "null island" sentinel. (0,0) is open ocean off Africa and never a real point
// here — it's the signature of a missing/zeroed coordinate, so plotting it draws
// a spurious line to the Gulf of Guinea.
function isValidLatLon(lat, lon) {
	if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
	if (Math.abs(lat) < 1e-6 && Math.abs(lon) < 1e-6) return false;
	return true;
}

function compareTravel(a, b) {
	const aso = toNumOrNull(a?.sort_order);
	const bso = toNumOrNull(b?.sort_order);
	if (aso != null && bso != null && aso !== bso) return aso - bso;
	if (aso != null && bso == null) return -1;
	if (aso == null && bso != null) return 1;

	const ast = String(a?.start_time || a?.end_time || '');
	const bst = String(b?.start_time || b?.end_time || '');
	if (ast !== bst) return ast.localeCompare(bst);

	const ac = String(a?.created_at || '');
	const bc = String(b?.created_at || '');
	if (ac !== bc) return ac.localeCompare(bc);

	return String(a?.activity_id || '').localeCompare(String(b?.activity_id || ''));
}

function pushStop(out, stop) {
	if (!stop) return;
	const lat = Number(stop.lat);
	const lon = Number(stop.lon);
	if (!isValidLatLon(lat, lon)) return;
	const k = stop.city || stop.key || `${lat.toFixed(3)},${lon.toFixed(3)}`;
	const prev = out[out.length - 1];
	if (prev && (prev.city === k || (prev.lat === lat && prev.lon === lon))) return;
	out.push({
		...stop,
		city: k,
		lat,
		lon,
	});
}

function pushLegStops(out, activityId, leg, legIdx) {
	const fromLat = toNumOrNull(leg?.from_lat);
	const fromLon = toNumOrNull(leg?.from_lon);
	const toLat = toNumOrNull(leg?.to_lat);
	const toLon = toNumOrNull(leg?.to_lon);

	const fromName = String(leg?.from_address || leg?.from_city || '').trim();
	const toName = String(leg?.to_address || leg?.to_city || '').trim();

	const fromKey = cityKeyFromText(fromName);
	const toKey = cityKeyFromText(toName);

	if (fromLat != null && fromLon != null && fromKey) {
		pushStop(out, {
			kind: 'travel',
			destination_id: `travel-from-${activityId}-leg-${legIdx}`,
			destination_name: fromName || 'From',
			key: fromKey,
			lat: fromLat,
			lon: fromLon,
		});
	}

	if (toLat != null && toLon != null && toKey) {
		pushStop(out, {
			kind: 'travel',
			destination_id: `travel-to-${activityId}-leg-${legIdx}`,
			destination_name: toName || 'To',
			key: toKey,
			lat: toLat,
			lon: toLon,
		});
	}
}

function approxDistDeg(a, b) {
	const dlat = a[0] - b[0];
	const dlon = (a[1] - b[1]) * Math.cos(((a[0] + b[0]) * Math.PI) / 360);
	return Math.sqrt(dlat * dlat + dlon * dlon);
}

function coversPair(travelStops, destA, destB, thresholdDeg = 1.5) {
	const aIdx = travelStops.findIndex((s) => approxDistDeg([s.lat, s.lon], destA) <= thresholdDeg);
	if (aIdx < 0) return false;
	return travelStops.slice(aIdx + 1).some((s) => approxDistDeg([s.lat, s.lon], destB) <= thresholdDeg);
}

function buildOrderedDestinationStops({ sortedDestinations, routePoints }) {
	const byId = new Map();
	(Array.isArray(routePoints) ? routePoints : []).forEach((p) => {
		if (p && p.destination_id) byId.set(String(p.destination_id), p);
	});

	const ordered = [];
	(Array.isArray(sortedDestinations) ? sortedDestinations : []).forEach((d) => {
		const p = byId.get(String(d.destination_id));
		if (!p) return;
		const lat = Number(p.lat);
		const lon = Number(p.lon);
		if (!isValidLatLon(lat, lon)) return;
		ordered.push({
			kind: 'destination',
			destination_id: p.destination_id,
			destination_name: p.destination_name || d.destination_name,
			city: cityKeyFromText(p.destination_name || d.destination_name) || `${lat.toFixed(3)},${lon.toFixed(3)}`,
			lat,
			lon,
		});
	});
	return ordered;
}

function buildTravelStops({ activities, isTravelTypeId }) {
	const list = Array.isArray(activities) ? activities : [];
	const travel = list.filter((a) => {
		if (typeof isTravelTypeId === 'function' && isTravelTypeId(a?.type_id)) return true;
		const legs = Array.isArray(a?.legs) ? a.legs : [];
		if (legs.length) return true;
		// Legacy single-leg travel: only when it ACTUALLY moves between two real
		// endpoints. A lone from/to (no valid second point) is a single-location
		// activity whose coordinate happens to live in from_* — not travel — so it
		// must not be drawn as a great-circle travel line. (This is why sightseeing
		// stops were rendering as straight lines instead of routed activity paths.)
		const fromOk = isValidLatLon(toNumOrNull(a?.from_lat), toNumOrNull(a?.from_lon));
		const toOk = isValidLatLon(toNumOrNull(a?.to_lat), toNumOrNull(a?.to_lon));
		return fromOk && toOk;
	});

	const ordered = [...travel].sort(compareTravel);
	const out = [];

	ordered.forEach((a, idx) => {
		const activityId = String(a?.activity_id || `idx-${idx}`);
		const legs = Array.isArray(a?.legs) ? a.legs : [];

		if (legs.length) {
			const sortedLegs = [...legs].sort((x, y) => (Number(x?.leg_index) || 0) - (Number(y?.leg_index) || 0));
			sortedLegs.forEach((leg, legIdx) => pushLegStops(out, activityId, leg, legIdx));
			return;
		}

		// Legacy single-leg travel stored on the activity.
		pushLegStops(out, activityId, a, 0);
	});

	return out;
}

export function buildTripRouteStops({
	sortedDestinations,
	routePoints,
	activities,
	isTravelTypeId,
	mode = 'auto',
}) {
	const orderedDestinationStops = buildOrderedDestinationStops({ sortedDestinations, routePoints });
	const travelStops = buildTravelStops({ activities, isTravelTypeId });

	const out = [];
	const seen = new Set();

	const add = (s) => {
		if (!s) return;
		const lat = Number(s.lat);
		const lon = Number(s.lon);
		if (!isValidLatLon(lat, lon)) return;
		const k = s.city || cityKeyFromText(s.destination_name) || `${lat.toFixed(3)},${lon.toFixed(3)}`;
		if (seen.has(k)) return;
		seen.add(k);
		out.push({ ...s, city: k, lat, lon });
	};

	const m = String(mode || 'auto');
	if (m === 'auto' || m === 'travel') {
		if (travelStops.length) travelStops.forEach(add);
		else orderedDestinationStops.forEach(add);
	} else if (m === 'travel+destinations') {
		if (travelStops.length) travelStops.forEach(add);
		orderedDestinationStops.forEach(add);
	} else {
		orderedDestinationStops.forEach(add);
	}

	// Travel segments: consecutive pairs from travelStops (for great circle arcs)
	const travelSegments = [];
	for (let i = 0; i < travelStops.length - 1; i++) {
		const a = travelStops[i];
		const b = travelStops[i + 1];
		if (isValidLatLon(a.lat, a.lon) && isValidLatLon(b.lat, b.lon)) {
			travelSegments.push([[a.lat, a.lon], [b.lat, b.lon]]);
		}
	}

	// Destination segments: consecutive dest pairs not already covered by travel stops
	const destinationSegments = [];
	for (let i = 0; i < orderedDestinationStops.length - 1; i++) {
		const a = orderedDestinationStops[i];
		const b = orderedDestinationStops[i + 1];
		if (!coversPair(travelStops, [a.lat, a.lon], [b.lat, b.lon])) {
			destinationSegments.push([[a.lat, a.lon], [b.lat, b.lon]]);
		}
	}

	return {
		stops: out,
		coords: out.map((s) => [s.lat, s.lon]),
		destinationSegments,
		travelSegments,
	};
}

